import { createAction, createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { TagDescription } from '@reduxjs/toolkit/query'

import { userScopeCleared } from '../session/scope'
import type { LiveEnvelope, LiveHandler, LiveStatus } from './types'

/**
 * Состояние живого канала С ТОЧКИ ЗРЕНИЯ ОБОЛОЧКИ: на связи или
 * переподключается. Событий здесь нет — они не данные, а повод перечитать.
 * Срок жизни — `screen`: после F5 канал подключается заново.
 */
export interface LiveState {
  status: LiveStatus
}

const initialState: LiveState = { status: 'off' }

const liveSlice = createSlice({
  name: 'live',
  initialState,
  reducers: {
    liveStatusChanged(state, action: PayloadAction<LiveStatus>) {
      state.status = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
  },
})

export const { liveStatusChanged } = liveSlice.actions
export const liveReducer = liveSlice.reducer

export const selectLiveStatus = (state: LiveState): LiveStatus => state.status

/**
 * Событие пришло. Обработчики пакетов уже перечитали свои теги; listener
 * пакета может подписаться на это действие, если ему нужно больше, чем теги.
 */
export const liveEventReceived = createAction<LiveEnvelope>('live/eventReceived')

/** Реестр обработчиков пакетов: тип события → что перечитать. */
export interface LiveHandlerRegistry {
  /** Теги к перечитыванию по событию (неизвестный тип — пусто). */
  invalidates(envelope: LiveEnvelope): TagDescription<string>[]
  /** Типы тегов к перечитыванию после переподключения. */
  resync(): string[]
}

/**
 * Собрать реестр из обработчиков модулей. Два обработчика одного типа — ошибка
 * сборки, а не «победил последний»: так расходились бы два пакета, не зная друг о друге.
 */
export function createLiveHandlerRegistry(handlers: readonly LiveHandler[]): LiveHandlerRegistry {
  const byType = new Map<string, LiveHandler>()
  for (const handler of handlers) {
    if (!/^[a-z][a-z0-9-]*(\.[a-z][a-z0-9-]*){1,5}$/.test(handler.type)) {
      throw new Error(`Тип события живого канала «${handler.type}» не по договору: <пространство>.<сущность>.<что случилось>`)
    }
    if (byType.has(handler.type)) throw new Error(`Обработчик события «${handler.type}» объявлен дважды`)
    byType.set(handler.type, handler)
  }
  const resync = [...new Set(handlers.flatMap((handler) => handler.resync))]
  return {
    invalidates: (envelope) => [...(byType.get(envelope.type)?.invalidates(envelope) ?? [])],
    resync: () => [...resync],
  }
}
